/*
 * ThermoAspen Interior Workspace 1.3.7
 * Multiple Windows Stages 2C-2F — carousel navigation + carousel-native window editing.
 *
 * Presentation/navigation authority only.
 * 1.3.7: eliminate redundant WebGL submissions and hidden-canvas draws.
 * Reads:
 *   - ThermoAspenSaunaSceneData / ThermoAspenSaunaSceneUpdated
 *   - ThermoAspenOpeningCollectionV2
 * Writes no project geometry or persisted project state.
 */
(function (W, D) {
  'use strict';

  var VERSION = '1.3.7';
  var THREE_VERSION = '0.185.1';
  var THREE_ESM = 'https://esm.sh/three@' + THREE_VERSION + '?target=es2022&bundle=false';
  var THREE_FALLBACK = 'https://cdn.jsdelivr.net/npm/three@' + THREE_VERSION + '/build/three.module.js';
  var WALLS = ['front', 'right', 'back', 'left'];
  var LABELS = { front: 'Front wall', right: 'Right wall', back: 'Back wall', left: 'Left wall' };
  var REDUCED_MOTION = false;
  try { REDUCED_MOTION = !!(W.matchMedia && W.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) {}

  if (W.ThermoAspenInteriorWorkspaceV1 && W.ThermoAspenInteriorWorkspaceV1.version === VERSION) return;
  if (W.ThermoAspenInteriorWorkspaceV1 && typeof W.ThermoAspenInteriorWorkspaceV1.destroy === 'function') {
    try { W.ThermoAspenInteriorWorkspaceV1.destroy(); } catch (e) {}
  }

  var state = {
    mode: 'room',
    activeWall: 'back',
    selectedOpeningId: null,
    sceneData: null,
    openings: [],
    revision: 0,
    ready: false,
    mounted: false,
    transitioning: false,
    lastAction: 'init',
    editorVisible: false,
    windowDraftActive: false
  };

  var ui = {
    root: null,
    stage: null,
    viewport: null,
    canvas: null,
    heading: null,
    intro: null,
    title: null,
    subtitle: null,
    close: null,
    prev: null,
    next: null,
    nav: null,
    live: null,
    loading: null,
    error: null,
    retry: null,
    mountTimer: 0,
    embedTimer: 0,
    previewStage: null,
    previewCanvas: null,
    previewPoster: null,
    previewError: null,
    switchButton: null,
    floorPlanButton: null,
    exteriorButton: null
  };

  var T = null;
  var renderer = null;
  var rendererStarting = false;
  var scene = null;
  var camera = null;
  var roomGroup = null;
  var benchGroup = null;
  var stoveGroup = null;
  var floorMesh = null;
  var ceilingMesh = null;
  var wallGroups = Object.create(null);
  var openingMeshes = [];
  var windowLabelSprites = [];
  var windowViewBaseTexture = null, windowViewLoading = false;
  var disposables = { geometries: [], materials: [], textures: [] };
  var resizeObserver = null;
  var resizeHandler = null;
  var previewStateObserver = null;
  var rebuildTimer = 0;
  var cameraFrame = 0;
  var wallSwitchPending = false;
  var wallSwitchEpoch = 0;
  var wallSwitchDestination = null;
  var wallSwitchPhase = null;
  var pointer = { down: false, id: null, x: 0, y: 0, startX: 0, startY: 0, moved: false };
  var raycaster = null;
  var mouse = null;

  function n(v, fallback) {
    v = Number(v);
    return isFinite(v) ? v : (fallback == null ? 0 : fallback);
  }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function mm(v) { return n(v) / 1000; }
  function clone(v) {
    try { return JSON.parse(JSON.stringify(v)); } catch (e) { return v; }
  }
  function wallIndex(wall) {
    var i = WALLS.indexOf(String(wall || '').toLowerCase());
    return i < 0 ? 2 : i;
  }
  function wallAt(i) {
    return WALLS[(i % WALLS.length + WALLS.length) % WALLS.length];
  }
  function wallLabel(wall) { return LABELS[wall] || String(wall || 'Wall'); }
  function openingLabel(o) {
    if (!o) return 'Opening';
    if (o.type === 'door') return 'Door';
    var m = String(o.id || '').match(/window\.(\d+)/i);
    return m ? 'Window ' + Number(m[1]) : 'Window';
  }
  function validScene(d) {
    return !!(d && d.room && n(d.room.lengthMm) > 0 && n(d.room.widthMm) > 0 && n(d.room.heightMm) > 0);
  }
  function roomMm() {
    var r = state.sceneData && state.sceneData.room || {};
    return { lengthMm: n(r.lengthMm), widthMm: n(r.widthMm), heightMm: n(r.heightMm) };
  }
  function roomM() {
    var r = roomMm();
    return { L: mm(r.lengthMm), W: mm(r.widthMm), H: mm(r.heightMm) };
  }
  function wallSpanM(wall, dims) {
    return wall === 'front' || wall === 'back' ? dims.L : dims.W;
  }
  function dispatch(name, detail) {
    try { W.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (e) {
      try {
        var ev = D.createEvent('CustomEvent');
        ev.initCustomEvent(name, false, false, detail);
        W.dispatchEvent(ev);
      } catch (x) {}
    }
  }
  function announce(text) {
    if (ui.live) ui.live.textContent = text || '';
  }

  function openingApi() { return W.ThermoAspenOpeningCollectionV2 || null; }
  function readOpenings() {
    var api = openingApi(), result = [];
    if (api && typeof api.getState === 'function') {
      try {
        var s = api.getState(roomMm());
        if (s && Array.isArray(s.openings)) {
          result = s.openings.filter(function (o) { return o && o.enabled !== false; }).map(clone);
          if (s.selectedWindowId) state.selectedOpeningId = s.selectedWindowId;
        }
      } catch (e) {}
    }
    if (!result.length && state.sceneData) {
      var sd = state.sceneData, layout = sd.layout || {}, op = sd.openings || {};
      if (layout.door) result.push({
        id: 'door.01', type: 'door', enabled: true, wall: layout.door.wall,
        offsetMm: n(layout.door.offsetMm), frameWidthMm: n(layout.door.widthMm, op.door && op.door.widthMm),
        frameHeightMm: n(layout.door.heightMm, op.door && op.door.heightMm), sillHeightMm: 0,
        hinge: layout.door.hinge, swing: layout.door.swing
      });
      if (layout.window && !(op.window && op.window.enabled === false)) result.push({
        id: 'window.01', type: 'window', enabled: true, wall: layout.window.wall,
        offsetMm: n(layout.window.offsetMm), frameWidthMm: n(layout.window.widthMm, op.window && op.window.widthMm),
        frameHeightMm: n(layout.window.heightMm, op.window && op.window.heightMm),
        sillHeightMm: n(layout.window.sillHeightMm)
      });
    }
    state.openings = result;
    return result;
  }
  function openingById(id) {
    id = String(id || '');
    for (var i = 0; i < state.openings.length; i++) if (state.openings[i].id === id) return state.openings[i];
    return null;
  }
  function chooseInitialWall() {
    readOpenings();
    var selected = openingById(state.selectedOpeningId);
    if (selected && WALLS.indexOf(selected.wall) >= 0) return selected.wall;
    var win = state.openings.find(function (o) { return o.type === 'window' && WALLS.indexOf(o.wall) >= 0; });
    if (win) return win.wall;
    var door = state.openings.find(function (o) { return o.type === 'door' && WALLS.indexOf(o.wall) >= 0; });
    return door ? door.wall : 'back';
  }
  function chooseCarouselEntryWall() {
    readOpenings();
    var door = state.openings.find(function (o) { return o.type === 'door' && WALLS.indexOf(o.wall) >= 0; });
    var preview = W.__ThermoAspen3DPreviewV17;
    var cameraData = null;
    if (preview && typeof preview.getCameraData === 'function') {
      try { cameraData = preview.getCameraData(); } catch (e) {}
    }
    // Keep the user's orientation when switching viewers. The Interior Viewer
    // exposes two canonical corner views: from the doorway and from the
    // opposite corner. In the carousel, activeWall is the wall being LOOKED AT.
    if (door && cameraData) {
      if (cameraData.view === 'doorwayCorner') return oppositeWall(door.wall);
      if (cameraData.view === 'oppositeCorner') return door.wall;
    }
    return WALLS.indexOf(state.activeWall) >= 0 ? state.activeWall : chooseInitialWall();
  }
  function selectOpening(id, opts) {
    opts = opts || {};
    var o = openingById(id);
    if (!o) return false;
    state.selectedOpeningId = o.id;
    if (o.type === 'window') {
      var api = openingApi();
      if (api && typeof api.selectWindow === 'function') {
        try { api.selectWindow(o.id, { reason: opts.reason || 'interior-workspace-select' }); } catch (e) {}
      }
    }
    state.revision++;
    state.lastAction = opts.reason || 'opening-select';
    updateChrome();
    scheduleRebuild('selection');
    emitUpdated();
    return true;
  }

  function buildMarkup() {
    var root = D.createElement('div');
    root.id = 'ti-interior-workspace';
    root.className = 'ti-card ti-card--sage ti-iw';
    root.setAttribute('data-ti-interior-workspace-version', VERSION);
    root.innerHTML = '' +
      '<div class="ti-iw__head">' +
        '<div class="ti-iw__head-copy"><span class="ti-eyebrow">Interior</span><h3 class="ti-card-title ti-iw__heading">Room view</h3><p class="ti-iw__intro">Choose a wall to edit.</p></div>' +
      '</div>' +
      '<div class="ti-iw__stage" role="application" aria-label="Interactive sauna interior wall navigator">' +
        '<div class="ti-iw__viewport"><canvas class="ti-iw__canvas" tabindex="0" aria-label="Sauna interior room view"></canvas></div>' +
        '<div class="ti-iw__loading"><span class="ti-iw__spinner" aria-hidden="true"></span><span>Preparing room view…</span></div>' +
        '<div class="ti-iw__error" hidden><strong>Room view unavailable</strong><span>The rest of the Sauna Designer is still available.</span><button type="button" class="ti-btn ti-btn--secondary ti-btn--compact ti-iw__retry">Try again</button></div>' +
        '<button type="button" class="ti-iw__arrow ti-iw__arrow--prev" aria-label="Turn to previous wall">‹</button>' +
        '<button type="button" class="ti-iw__arrow ti-iw__arrow--next" aria-label="Turn to next wall">›</button>' +
        '<button type="button" class="ti-iw__close" aria-label="Return to room view" title="Return to room view" hidden>×</button>' +
        '<div class="ti-iw__wall-chip"><strong class="ti-iw__wall-title">Back wall</strong><span class="ti-iw__wall-subtitle">Click to edit</span></div>' +
      '</div>' +
      '<div class="ti-iw__live" aria-live="polite"></div>';
    return root;
  }

  function setWindowDraftActive(active) {
    active = !!active;
    if (state.windowDraftActive === active) return true;
    state.windowDraftActive = active;
    if (ui.root) ui.root.classList.toggle('has-window-draft', active);
    updateChrome();
    emitUpdated();
    return true;
  }

  function blockDraftNavigation(reason) {
    dispatch('ThermoAspenWindowDraftNavigationBlocked', { reason: reason || 'wall-change', activeWall: state.activeWall });
    announce('Confirm the window or use X before changing wall.');
    return false;
  }

  function realWindowCount() {
    return (state.openings || []).filter(function (o) { return o && o.type === 'window' && o.enabled !== false; }).length;
  }

  function interiorViewerOpen() {
    // The Interior Preview owns loading/readiness. Its authoritative visible
    // state is: real canvas shown, loading poster hidden, error hidden.
    var c = ui.previewCanvas, p = ui.previewPoster, e = ui.previewError;
    return !!(c && c.hidden === false && (!p || p.hidden === true) && (!e || e.hidden === true));
  }

  function updateViewerSwitchButton() {
    var active = !!state.editorVisible, viewerOpen = interiorViewerOpen();
    if (ui.switchButton) {
      var hasWindows = realWindowCount() > 0;
      var idleLabel = hasWindows ? 'Adjust Windows' : 'Add Windows';
      ui.switchButton.textContent = active ? 'Close' : idleLabel;
      ui.switchButton.setAttribute('aria-label', active ? 'Close window editor' : (hasWindows ? 'Adjust existing windows' : 'Add windows'));
      ui.switchButton.setAttribute('aria-pressed', active ? 'true' : 'false');
      ui.switchButton.title = active ? 'Return to 3D interior viewer' : (hasWindows ? 'Adjust window positions and sizes' : 'Add windows to this sauna');
      // Never show Add/Adjust during the poster/loading/error state. Close stays
      // available once the editor itself is open.
      ui.switchButton.hidden = !(active || viewerOpen);
    }
    var viewJumpVisible = viewerOpen && !active;
    if (ui.floorPlanButton) ui.floorPlanButton.hidden = !viewJumpVisible;
    if (ui.exteriorButton) ui.exteriorButton.hidden = !viewJumpVisible;
    if (ui.previewStage) ui.previewStage.classList.toggle('ti3d-exterior-button-visible', viewJumpVisible);
  }

  function observePreviewReadyState() {
    if (previewStateObserver) { try { previewStateObserver.disconnect(); } catch (e) {} previewStateObserver = null; }
    if (!W.MutationObserver) { updateViewerSwitchButton(); return; }
    previewStateObserver = new W.MutationObserver(function () { updateViewerSwitchButton(); });
    [ui.previewCanvas, ui.previewPoster, ui.previewError].forEach(function (el) {
      if (el) previewStateObserver.observe(el, { attributes: true, attributeFilter: ['hidden'] });
    });
    updateViewerSwitchButton();
  }

  function applyOverlayState(show) {
    if (!ui.root || !ui.previewStage) return;
    ui.root.removeAttribute('hidden');
    ui.root.style.setProperty('display', 'block', 'important');
    ui.root.style.setProperty('position', 'absolute', 'important');
    ui.root.style.setProperty('inset', '0', 'important');
    ui.root.style.setProperty('z-index', '20', 'important');
    ui.root.style.setProperty('margin', '0', 'important');
    ui.root.style.setProperty('padding', '0', 'important');
    ui.root.style.setProperty('border', '0', 'important');
    ui.root.style.setProperty('border-radius', 'inherit', 'important');
    ui.root.style.setProperty('overflow', 'hidden', 'important');
    ui.root.style.setProperty('background', '#eee9df', 'important');
    ui.root.style.setProperty('visibility', show ? 'visible' : 'hidden', 'important');
    ui.root.style.setProperty('opacity', show ? '1' : '0', 'important');
    ui.root.style.setProperty('pointer-events', show ? 'auto' : 'none', 'important');
    if (ui.stage) {
      ui.stage.style.setProperty('position', 'absolute', 'important');
      ui.stage.style.setProperty('inset', '0', 'important');
      ui.stage.style.setProperty('width', '100%', 'important');
      ui.stage.style.setProperty('height', '100%', 'important');
      ui.stage.style.setProperty('min-height', '0', 'important');
      ui.stage.style.setProperty('max-height', 'none', 'important');
      ui.stage.style.setProperty('border', '0', 'important');
      ui.stage.style.setProperty('border-radius', 'inherit', 'important');
    }
  }

  function setEditorVisible(show, opts) {
    opts = opts || {};
    show = !!show;
    if (!ui.root || !ui.previewStage) return false;
    if (show) {
      // Edit always enters the CAROUSEL first. This preserves orientation and
      // lets the user choose a wall deliberately before editing an opening.
      cancelFocusedWallSwitch();
      var entryWall = chooseCarouselEntryWall();
      var changed = state.mode !== 'room' || state.activeWall !== entryWall;
      state.mode = 'room';
      state.activeWall = entryWall;
      if (changed) state.revision++;
      state.lastAction = opts.reason || 'editor-open';
    } else {
      cancelFocusedWallSwitch();
      if (state.mode !== 'room') {
        state.mode = 'room';
        state.revision++;
        state.lastAction = opts.reason || 'editor-close';
      }
    }
    state.editorVisible = show;
    if (!show) state.windowDraftActive = false;
    ui.root.classList.toggle('is-editor-visible', show);
    ui.root.classList.toggle('has-window-draft', !!state.windowDraftActive);
    ui.root.setAttribute('aria-hidden', show ? 'false' : 'true');
    ui.previewStage.classList.toggle('ti3d-editor-active', show);
    applyOverlayState(show);
    updateVisibility();
    updateChrome();
    updateViewerSwitchButton();
    if (show && !state.ready && !renderer) startRenderer();
    if (state.ready) {
      if (show) {
        // Update canvas dimensions before drawing. The camera change below
        // produces the ONE visible WebGL frame needed for this toggle.
        resize(true, true);
        setCameraForCurrent(false);
      } else {
        // Closing the editor must also stop a camera transition in progress.
        // Keep its pose current, but do not render or resize the hidden canvas.
        cancelCameraTransition();
        setCameraForCurrent(false);
      }
      emitUpdated();
    }
    if (show) {
      announce('Window editor opened in carousel view facing ' + wallLabel(state.activeWall) + '. Add or select a window on the centred wall.');
    } else if (!opts.initial) {
      announce('Returned to the 3D interior viewer.');
    }
    return true;
  }

  function attachToPreview() {
    if (!ui.root) return false;
    var stage = D.getElementById('ti3d-stage');
    if (!stage) return false;
    ui.previewStage = stage;
    ui.previewCanvas = stage.querySelector('#ti3d-canvas');
    ui.previewPoster = stage.querySelector('#ti3d-poster');
    ui.previewError = stage.querySelector('#ti3d-error');
    if (ui.root.parentNode !== stage) stage.appendChild(ui.root);
    ui.root.classList.remove('ti-iw--awaiting-overlay');
    ui.root.classList.add('ti-iw--overlay');
    var oldButton = stage.querySelector('.ti3d-edit-toggle');
    if (oldButton && oldButton.parentNode) oldButton.parentNode.removeChild(oldButton);
    var oldFloorPlan = stage.querySelector('.ti3d-floorplan-jump');
    if (oldFloorPlan && oldFloorPlan.parentNode) oldFloorPlan.parentNode.removeChild(oldFloorPlan);
    var oldExterior = stage.querySelector('.ti3d-exterior-jump');
    if (oldExterior && oldExterior.parentNode) oldExterior.parentNode.removeChild(oldExterior);
    var button = D.createElement('button');
    button.type = 'button';
    button.className = 'ti3d-edit-toggle';
    button.hidden = true;
    stage.appendChild(button);
    ui.switchButton = button;
    var floorPlan = D.createElement('button');
    floorPlan.type = 'button';
    floorPlan.className = 'ti-view-jump ti3d-floorplan-jump';
    floorPlan.textContent = 'Floor Plan';
    floorPlan.setAttribute('aria-label', 'Jump to the floor plan');
    floorPlan.title = 'View the floor plan';
    floorPlan.hidden = true;
    stage.appendChild(floorPlan);
    ui.floorPlanButton = floorPlan;
    var exterior = D.createElement('button');
    exterior.type = 'button';
    exterior.className = 'ti-view-jump ti3d-exterior-jump';
    exterior.textContent = 'Exterior';
    exterior.setAttribute('aria-label', 'Jump to the 3D exterior viewer');
    exterior.title = 'View the sauna exterior';
    exterior.hidden = true;
    stage.appendChild(exterior);
    ui.exteriorButton = exterior;
    ['pointerdown','pointerup','pointercancel','mousedown','mouseup','touchstart','touchend'].forEach(function (type) {
      button.addEventListener(type, function (e) { if (e) e.stopPropagation(); });
      floorPlan.addEventListener(type, function (e) { if (e) e.stopPropagation(); });
      exterior.addEventListener(type, function (e) { if (e) e.stopPropagation(); });
    });
    button.addEventListener('click', function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      setEditorVisible(!state.editorVisible, { reason: state.editorVisible ? 'viewer-close-button' : 'viewer-edit-button' });
    });
    floorPlan.addEventListener('click', function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      var target = D.getElementById('ti-sauna-layout');
      if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
    });
    exterior.addEventListener('click', function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      var target = D.getElementById('ti-sauna-exterior-viewer');
      if (target && typeof target.scrollIntoView === 'function') target.scrollIntoView({ behavior: REDUCED_MOTION ? 'auto' : 'smooth', block: 'start' });
    });
    observePreviewReadyState();
    applyOverlayState(false);
    setEditorVisible(false, { initial: true, reason: 'viewer-attach' });
    resize(true);
    return true;
  }

  function attachToPreviewWhenReady() {
    if (attachToPreview()) return;
    clearTimeout(ui.embedTimer);
    ui.embedTimer = W.setTimeout(attachToPreviewWhenReady, 120);
  }

  function mount() {
    if (state.mounted) return true;
    var host = D.getElementById('ti-custom-body');
    if (!host) return false;
    var existing = D.getElementById('ti-interior-workspace');
    if (existing) existing.remove();
    ui.root = buildMarkup();
    ui.root.classList.add('ti-iw--awaiting-overlay');
    ui.root.setAttribute('aria-hidden', 'true');
    var planCard = host.querySelector('.ti-layout-plan-card');
    if (planCard) { if (planCard.nextSibling) host.insertBefore(ui.root, planCard.nextSibling); else host.appendChild(ui.root); } else host.insertBefore(ui.root, host.firstChild);
    ui.stage = ui.root.querySelector('.ti-iw__stage');
    ui.viewport = ui.root.querySelector('.ti-iw__viewport');
    ui.heading = ui.root.querySelector('.ti-iw__heading');
    ui.intro = ui.root.querySelector('.ti-iw__intro');
    ui.canvas = ui.root.querySelector('.ti-iw__canvas');
    ui.title = ui.root.querySelector('.ti-iw__wall-title');
    ui.subtitle = ui.root.querySelector('.ti-iw__wall-subtitle');
    ui.close = ui.root.querySelector('.ti-iw__close');
    ui.prev = ui.root.querySelector('.ti-iw__arrow--prev');
    ui.next = ui.root.querySelector('.ti-iw__arrow--next');
    ui.nav = ui.root.querySelector('.ti-iw__nav');
    ui.live = ui.root.querySelector('.ti-iw__live');
    ui.loading = ui.root.querySelector('.ti-iw__loading');
    ui.error = ui.root.querySelector('.ti-iw__error');
    ui.retry = ui.root.querySelector('.ti-iw__retry');
    ui.prev.addEventListener('click', function () { navigateAdjacent(-1, 'previous-button'); });
    ui.next.addEventListener('click', function () { navigateAdjacent(1, 'next-button'); });
    ui.close.addEventListener('click', function () { setEditorVisible(false, { reason: 'legacy-close-button' }); });
    ui.retry.addEventListener('click', function () { restartRenderer(); });
    ui.canvas.addEventListener('pointerdown', onPointerDown);
    ui.canvas.addEventListener('pointermove', onPointerMove);
    ui.canvas.addEventListener('pointerup', onPointerUp);
    ui.canvas.addEventListener('pointercancel', onPointerCancel);
    ['pointerdown','pointermove','pointerup','pointercancel','wheel'].forEach(function (type) {
      ui.root.addEventListener(type, function (e) { if (e) e.stopPropagation(); });
    });
    ui.stage.addEventListener('keydown', onKeyDown);
    state.mounted = true;
    state.activeWall = chooseInitialWall();
    updateChrome();
    // Renderer is deliberately lazy: the hidden carousel does not create a
    // second WebGL context until the user actually opens window editing.
    attachToPreviewWhenReady();
    return true;
  }

  function mountWhenReady() {
    if (mount()) return;
    clearTimeout(ui.mountTimer);
    ui.mountTimer = W.setTimeout(mountWhenReady, 120);
  }

  function updateChrome() {
    if (!ui.root) return;
    var focus = state.mode === 'wall';
    var carouselEdit = !!(state.editorVisible && state.mode === 'room');
    ui.root.classList.toggle('is-wall-mode', focus);
    ui.root.classList.toggle('is-carousel-window-edit', carouselEdit);
    ui.root.classList.toggle('has-window-draft', !!state.windowDraftActive);
    if (ui.previewStage) {
      ui.previewStage.classList.toggle('ti3d-wall-edit-active', focus);
      var previewCard = ui.previewStage.closest && ui.previewStage.closest('.ti3d-card');
      if (previewCard) previewCard.classList.toggle('ti3d-wall-edit-card-active', focus);
    }
    if (ui.heading) ui.heading.textContent = wallSwitchPending ? 'Changing wall' : (focus ? 'Wall view' : (carouselEdit ? 'Window editor' : 'Room view'));
    if (ui.intro) ui.intro.textContent = wallSwitchPending ? 'Turning to the adjacent wall…' : (focus ? 'Select a door or window, or change walls with the arrows.' : (carouselEdit ? 'Add or select a window on the centred wall. Use the arrows to change walls.' : 'Choose a wall to edit.'));
    if (ui.title) ui.title.textContent = wallSwitchPending && wallSwitchPhase === 'turn' ? 'Turning to ' + wallLabel(wallSwitchDestination) : wallLabel(state.activeWall);
    if (ui.subtitle) ui.subtitle.textContent = wallSwitchPending ? 'Please wait' : (focus ? 'Select an opening' : (carouselEdit ? (state.windowDraftActive ? 'Finish or cancel the window draft' : 'Add or select a window') : 'Click to edit'));
    if (ui.close) ui.close.hidden = true;
    if (ui.stage) ui.stage.setAttribute('aria-busy', wallSwitchPending ? 'true' : 'false');
    if (ui.prev) {
      ui.prev.hidden = false;
      ui.prev.disabled = wallSwitchPending;
      ui.prev.style.opacity = wallSwitchPending ? '.55' : '';
      ui.prev.setAttribute('aria-label', (focus ? 'View ' : 'Turn to ') + wallLabel(adjacentWall(state.activeWall, -1)));
      ui.prev.title = focus ? 'Previous wall: ' + wallLabel(adjacentWall(state.activeWall, -1)) : 'Turn left';
    }
    if (ui.next) {
      ui.next.hidden = false;
      ui.next.disabled = wallSwitchPending;
      ui.next.style.opacity = wallSwitchPending ? '.55' : '';
      ui.next.setAttribute('aria-label', (focus ? 'View ' : 'Turn to ') + wallLabel(adjacentWall(state.activeWall, 1)));
      ui.next.title = focus ? 'Next wall: ' + wallLabel(adjacentWall(state.activeWall, 1)) : 'Turn right';
    }
    if (ui.nav) {
      Array.prototype.forEach.call(ui.nav.querySelectorAll('[data-wall]'), function (b) {
        var active = b.getAttribute('data-wall') === state.activeWall;
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.disabled = false;
        b.tabIndex = active ? 0 : -1;
      });
      ui.nav.hidden = focus || wallSwitchPending;
    }
    if (ui.canvas) {
      ui.canvas.setAttribute('aria-label', focus ? wallLabel(state.activeWall) + ' focused wall view' : (carouselEdit ? 'Window editor carousel facing ' + wallLabel(state.activeWall) : 'Sauna room view facing ' + wallLabel(state.activeWall)));
    }
  }

  function showLoading(show) {
    if (ui.loading) ui.loading.hidden = !show;
  }
  function showError(show) {
    if (ui.error) ui.error.hidden = !show;
  }

  function getThree() {
    function unwrap(bundle) { return bundle && bundle.three ? bundle.three : bundle; }
    if (W.__ThermoAspenThreePromise) return W.__ThermoAspenThreePromise.then(unwrap);
    W.__ThermoAspenThreePromise = import(THREE_ESM).catch(function () { return import(THREE_FALLBACK); });
    return W.__ThermoAspenThreePromise.then(unwrap).catch(function (err) {
      W.__ThermoAspenThreePromise = null;
      throw err;
    });
  }

  async function startRenderer() {
    if (!ui.canvas || renderer || rendererStarting) return;
    rendererStarting = true;
    showError(false);
    showLoading(true);
    try {
      T = await getThree();
      if (!state.mounted || !ui.canvas || !state.editorVisible) { rendererStarting = false; showLoading(false); return; }
      renderer = new T.WebGLRenderer({ canvas: ui.canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(W.devicePixelRatio || 1, 1.5));
      renderer.outputColorSpace = T.SRGBColorSpace;
      renderer.toneMapping = T.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.02;
      renderer.setClearColor(0xeee9df, 1);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = T.PCFSoftShadowMap;
      scene = new T.Scene();
      scene.background = new T.Color(0xeee9df);
      // Preload the same exterior window-view image used by the main Interior 3D Preview.
      loadWindowViewTexture();
      camera = new T.PerspectiveCamera(43, 1, 0.02, 50);
      camera.rotation.order = 'YXZ';
      raycaster = new T.Raycaster();
      mouse = new T.Vector2();
      var hemi = new T.HemisphereLight(0xfff8ed, 0x7b7065, 1.8);
      scene.add(hemi);
      var key = new T.DirectionalLight(0xfff4df, 2.0);
      key.position.set(3.5, 5.4, 4.2);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      key.shadow.camera.near = 0.1;
      key.shadow.camera.far = 15;
      scene.add(key);
      var fill = new T.DirectionalLight(0xdde8ea, 0.65);
      fill.position.set(-3, 2.8, -2);
      scene.add(fill);
      if ('ResizeObserver' in W) {
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(ui.viewport);
      } else {
        resizeHandler = resize;
        W.addEventListener('resize', resizeHandler);
      }
      state.ready = true;
      rendererStarting = false;
      state.mode = 'room';
      state.activeWall = state.editorVisible ? chooseCarouselEntryWall() : chooseInitialWall();
      rebuildScene('renderer-start');
      resize();
      // If Edit was pressed before Three.js finished loading, honour the
      // pending editor state by opening the carousel, never a focused wall.
      if (state.editorVisible) {
        updateVisibility();
        updateChrome();
        setCameraForCurrent(false);
      }
      showLoading(false);
      emitReady();
    } catch (err) {
      rendererStarting = false;
      state.ready = false;
      showLoading(false);
      showError(true);
      try { console.error('ThermoAspen Interior Workspace:', err); } catch (e) {}
    }
  }

  function restartRenderer() {
    disposeRenderer();
    startRenderer();
  }

  function trackGeometry(g) { disposables.geometries.push(g); return g; }
  function trackMaterial(m) { disposables.materials.push(m); return m; }
  function trackTexture(t) { disposables.textures.push(t); return t; }
  function windowViewUrl(){return '/cdn/shop/files/AI_Window_View.png';}
  function loadWindowViewTexture(){
    if(!T||!renderer)return null;if(windowViewBaseTexture)return windowViewBaseTexture;if(windowViewLoading)return null;windowViewLoading=true;
    try{new T.TextureLoader().load(windowViewUrl(),function(tex){tex.colorSpace=T.SRGBColorSpace;tex.wrapS=tex.wrapT=T.ClampToEdgeWrapping;tex.anisotropy=Math.min(8,renderer.capabilities.getMaxAnisotropy());tex.needsUpdate=true;windowViewBaseTexture=tex;windowViewLoading=false;if(state.ready)scheduleRebuild('window-landscape-loaded');},undefined,function(){windowViewLoading=false;});}catch(e){windowViewLoading=false;}return null;
  }
  function addWindowLandscape(group,o,dims,ow,oh,uc,yc){
    var tex=loadWindowViewTexture();if(!tex||o.type!=='window')return;
    var wallW=(o.wall==='back'||o.wall==='front')?dims.L:dims.W,wallH=dims.H,left=clamp((uc-ow/2)/wallW,0,1),bottom=clamp((yc-oh/2)/wallH,0,1),ww=clamp(ow/wallW,0,1),wh=clamp(oh/wallH,0,1),t=tex.clone();
    var iw=(tex.image&&tex.image.width)||1,ih=(tex.image&&tex.image.height)||1,iar=iw/ih,war=wallW/wallH,rx=1,ry=1,ox=0,oy=0,scale=1.08;
    if(iar>war){rx=war/iar;ox=(1-rx)/2}else{ry=iar/war;oy=(1-ry)/2}rx/=scale;ry/=scale;ox+=(1-rx)/2-(1-(iar>war?war/iar:1)/scale)/2;oy+=(1-ry)/2-(1-(iar>war?1:iar/war)/scale)/2;
    trackTexture(t);t.colorSpace=T.SRGBColorSpace;t.wrapS=t.wrapT=T.ClampToEdgeWrapping;t.repeat.set(rx*ww,ry*wh);t.offset.set(ox+rx*left,oy+ry*bottom);t.needsUpdate=true;
    var mat=trackMaterial(new T.MeshBasicMaterial({map:t,color:0xffffff,side:T.DoubleSide,toneMapped:true}));wallBox(group,o.wall,ow*1.06,oh*1.06,.004,mat,uc,yc,-.16,dims,null);
  }
  function disposeSceneContent() {
    if (roomGroup && scene) scene.remove(roomGroup);
    disposables.geometries.forEach(function (g) { try { g.dispose(); } catch (e) {} });
    disposables.materials.forEach(function (m) { try { m.dispose(); } catch (e) {} });
    disposables.textures.forEach(function (t) { try { t.dispose(); } catch (e) {} });
    disposables = { geometries: [], materials: [], textures: [] };
    roomGroup = benchGroup = stoveGroup = floorMesh = ceilingMesh = null;
    windowLabelSprites = [];
    wallGroups = Object.create(null);
    openingMeshes = [];
  }

  function disposeRenderer() {
    rendererStarting = false;
    cancelFocusedWallSwitch();
    cancelAnimationFrame(cameraFrame);
    cameraFrame = 0;
    clearTimeout(rebuildTimer);
    rebuildTimer = 0;
    disposeSceneContent();
    if (resizeObserver) { try { resizeObserver.disconnect(); } catch (e) {} resizeObserver = null; }
    if (resizeHandler) { W.removeEventListener('resize', resizeHandler); resizeHandler = null; }
    if (renderer) {
      try { renderer.dispose(); renderer.forceContextLoss(); } catch (e) {}
    }
    renderer = scene = camera = raycaster = mouse = T = null;
    state.ready = false;
  }

  var woodCanvasRole='wall';
  function woodCanvas(direction) {
    var lib=W.ThermoAspenTimberMaterialsV1;
    if(lib){
      var role=woodCanvasRole||'wall',species=lib.resolveSpecies(state.sceneData,role);
      return trackTexture(lib.createCladdingTexture(T,renderer,{speciesId:species,role:role,direction:direction}));
    }
    var c = D.createElement('canvas'), ctx = c.getContext('2d'), size = 512, boards = 8, step = size / boards;
    c.width = c.height = size;
    ctx.fillStyle = '#ddb994';
    ctx.fillRect(0, 0, size, size);
    for (var i = 0; i < boards; i++) {
      var p = i * step;
      var light = 208 + (i % 3) * 5;
      var red = 205 + (i % 4) * 5;
      ctx.fillStyle = 'rgb(' + red + ',' + light + ',' + (174 + (i % 2) * 8) + ')';
      if (direction === 'horizontal') ctx.fillRect(0, p, size, step + 1); else ctx.fillRect(p, 0, step + 1, size);
      ctx.fillStyle = 'rgba(73,45,28,.55)';
      if (direction === 'horizontal') ctx.fillRect(0, p, size, 2.2); else ctx.fillRect(p, 0, 2.2, size);
      ctx.fillStyle = 'rgba(255,244,225,.17)';
      if (direction === 'horizontal') ctx.fillRect(0, p + 3, size, 1); else ctx.fillRect(p + 3, 0, 1, size);
      ctx.strokeStyle = 'rgba(100,62,37,.10)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (var k = 0; k < 5; k++) {
        var q = p + step * (0.18 + k * 0.15);
        if (direction === 'horizontal') { ctx.moveTo(0, q); ctx.bezierCurveTo(size * .3, q + 6, size * .7, q - 7, size, q + 2); }
        else { ctx.moveTo(q, 0); ctx.bezierCurveTo(q + 6, size * .3, q - 6, size * .7, q + 2, size); }
      }
      ctx.stroke();
    }
    var t = new T.CanvasTexture(c);
    t.wrapS = t.wrapT = T.RepeatWrapping;
    t.colorSpace = T.SRGBColorSpace;
    t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    t.userData.boardCount = boards;
    return trackTexture(t);
  }

  function unique(values) {
    values.sort(function (a, b) { return a - b; });
    return values.filter(function (v, i) { return i === 0 || Math.abs(v - values[i - 1]) > 0.0005; });
  }

  function wallOpenings(wall, dims) {
    var span = wallSpanM(wall, dims), out = [];
    readOpenings();
    state.openings.forEach(function (o) {
      if (!o || o.enabled === false || o.wall !== wall) return;
      var width = mm(o.frameWidthMm || o.widthMm), height = mm(o.frameHeightMm || o.heightMm);
      // Clip the visible wall aperture to the finished room; opening placement
      // itself is validated against the actual structural and wall limits.
      var rawU0 = mm(o.offsetMm), rawU1 = rawU0 + width;
      var rawV0 = o.type === 'window' ? mm(o.sillHeightMm) : 0;
      var rawV1 = rawV0 + height;
      var u0 = clamp(rawU0, 0, span), u1 = clamp(rawU1, 0, span);
      var v0 = clamp(rawV0, 0, dims.H), v1 = clamp(rawV1, 0, dims.H);
      if (u1 - u0 > .03 && v1 - v0 > .03) out.push({ id: o.id, type: o.type || 'window', wall: wall, u0: u0, u1: u1, v0: v0, v1: v1, frameU0: rawU0, frameU1: rawU1, frameV0: rawV0, frameV1: rawV1, data: o });
    });
    return out;
  }

  function wallPlane(group, wall, w, h, mat, u, y, dims) {
    var mesh = new T.Mesh(trackGeometry(new T.PlaneGeometry(Math.max(.002, w), Math.max(.002, h))), mat);
    if (wall === 'back') mesh.position.set(u, y, .008);
    else if (wall === 'front') { mesh.position.set(u, y, dims.W - .008); mesh.rotation.y = Math.PI; }
    else if (wall === 'left') { mesh.position.set(.008, y, u); mesh.rotation.y = Math.PI / 2; }
    else { mesh.position.set(dims.L - .008, y, u); mesh.rotation.y = -Math.PI / 2; }
    mesh.receiveShadow = true;
    mesh.userData.thermoAspenWall = wall;
    group.add(mesh);
    return mesh;
  }

  function wallBox(group, wall, width, height, depth, mat, u, y, normal, dims, data) {
    var geo, mesh;
    if (wall === 'front' || wall === 'back') {
      geo = trackGeometry(new T.BoxGeometry(Math.max(.002, width), Math.max(.002, height), Math.max(.002, depth)));
      mesh = new T.Mesh(geo, mat);
      mesh.position.set(u, y, wall === 'back' ? normal : dims.W - normal);
    } else {
      geo = trackGeometry(new T.BoxGeometry(Math.max(.002, depth), Math.max(.002, height), Math.max(.002, width)));
      mesh = new T.Mesh(geo, mat);
      mesh.position.set(wall === 'left' ? normal : dims.L - normal, y, u);
    }
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.thermoAspenWall = wall;
    if (data && data.id) mesh.userData.thermoAspenOpeningId = data.id;
    if (data && data.type) mesh.userData.thermoAspenOpeningType = data.type;
    group.add(mesh);
    return mesh;
  }

  function addOpening(group, o, dims) {
    var selected = o.id === state.selectedOpeningId;
    var frame = trackMaterial(new T.MeshStandardMaterial({ color: selected ? 0x26362a : 0x6b5a4d, roughness: .72, metalness: 0 }));
    var glass = trackMaterial(new T.MeshPhysicalMaterial({ color: o.type === 'door' ? 0x514c48 : 0xbccbd0, roughness: .18, metalness: 0, transparent: true, opacity: o.type === 'door' ? .52 : .42, transmission: o.type === 'window' ? .26 : .08, thickness: .015, depthWrite: false, side: T.DoubleSide }));
    var recess = trackMaterial(new T.MeshStandardMaterial({ color: 0x3a3531, roughness: .95, metalness: 0 }));
    var ow = o.frameU1 - o.frameU0, oh = o.frameV1 - o.frameV0, uc = (o.frameU0 + o.frameU1) / 2, yc = (o.frameV0 + o.frameV1) / 2;
    var bar = selected ? .042 : .032, d = .035;
    if(o.type==='window')addWindowLandscape(group,o,dims,ow,oh,uc,yc);
    wallBox(group, o.wall, Math.max(.04, ow - bar * 2), Math.max(.04, oh - bar * 2), .012, recess, uc, yc, -.028, dims, o);
    wallBox(group, o.wall, Math.max(.04, ow - bar * 2), Math.max(.04, oh - bar * 2), .006, glass, uc, yc, -.018, dims, o);
    wallBox(group, o.wall, ow, bar, d, frame, uc, o.frameV0 + bar / 2, .012, dims, o);
    wallBox(group, o.wall, ow, bar, d, frame, uc, o.frameV1 - bar / 2, .012, dims, o);
    wallBox(group, o.wall, bar, Math.max(.04, oh - bar * 2), d, frame, o.frameU0 + bar / 2, yc, .012, dims, o);
    wallBox(group, o.wall, bar, Math.max(.04, oh - bar * 2), d, frame, o.frameU1 - bar / 2, yc, .012, dims, o);
    if (o.type === 'window') addWindowLabel(group, o, uc, yc, dims);
  }

  function addWindowLabel(group, o, u, y, dims) {
    var c = D.createElement('canvas'), ctx = c.getContext('2d');
    c.width = 256; c.height = 112;
    ctx.fillStyle = 'rgba(247,245,239,.94)';
    roundRect(ctx, 10, 10, 236, 92, 22); ctx.fill();
    ctx.strokeStyle = o.id === state.selectedOpeningId ? '#26362a' : 'rgba(38,54,42,.34)';
    ctx.lineWidth = o.id === state.selectedOpeningId ? 7 : 4;
    roundRect(ctx, 10, 10, 236, 92, 22); ctx.stroke();
    ctx.fillStyle = '#26362a';
    ctx.font = '700 40px system-ui, -apple-system, Segoe UI, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    var m = String(o.id || '').match(/window\.(\d+)/i);
    ctx.fillText(m ? 'W' + Number(m[1]) : 'WINDOW', 128, 57);
    var tex = trackTexture(new T.CanvasTexture(c)); tex.colorSpace = T.SRGBColorSpace;
    var mat = trackMaterial(new T.SpriteMaterial({ map: tex, transparent: true, depthTest: true, depthWrite: false }));
    var sprite = new T.Sprite(mat), p = wallPoint(o.wall, dims, u, y, .055);
    sprite.position.set(p.x, p.y, p.z); sprite.scale.set(.28, .122, 1); sprite.renderOrder = 5;
    sprite.userData.thermoAspenOpeningId = o.id; sprite.userData.thermoAspenOpeningType = o.type; sprite.userData.thermoAspenWall = o.wall;
    sprite.visible = state.mode !== 'wall';
    group.add(sprite); openingMeshes.push(sprite); windowLabelSprites.push(sprite);
  }

  function roundRect(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }

  function wallPoint(wall, dims, u, y, normal) {
    if (wall === 'back') return { x: u, y: y, z: normal };
    if (wall === 'front') return { x: u, y: y, z: dims.W - normal };
    if (wall === 'left') return { x: normal, y: y, z: u };
    return { x: dims.L - normal, y: y, z: u };
  }

  function addWall(wall, dims, textureBase, direction, coverage) {
    var group = new T.Group(); group.name = 'wall-' + wall; group.userData.thermoAspenWall = wall;
    wallGroups[wall] = group; roomGroup.add(group);
    var span = wallSpanM(wall, dims), ops = wallOpenings(wall, dims), xs = [0, span], ys = [0, dims.H];
    ops.forEach(function (o) { xs.push(o.u0, o.u1); ys.push(o.v0, o.v1); });
    xs = unique(xs); ys = unique(ys);
    var boards = textureBase.userData.boardCount || 8;
    var boardPeriod = Math.max(.05, coverage) * boards, shared=W.ThermoAspenTimberMaterialsV1;
    var runLength=shared?2.4:1.8;
    for (var i = 0; i < xs.length - 1; i++) for (var j = 0; j < ys.length - 1; j++) {
      var u0 = xs[i], u1 = xs[i + 1], v0 = ys[j], v1 = ys[j + 1], um = (u0 + u1) / 2, vm = (v0 + v1) / 2, inside = false;
      for (var k = 0; k < ops.length; k++) if (um > ops[k].u0 && um < ops[k].u1 && vm > ops[k].v0 && vm < ops[k].v1) { inside = true; break; }
      if (inside || u1 - u0 < .002 || v1 - v0 < .002) continue;
      var tex = textureBase.clone(); trackTexture(tex); tex.needsUpdate = true; tex.wrapS = tex.wrapT = T.RepeatWrapping;
      if (direction === 'horizontal') {
        tex.repeat.set(Math.max(.2, (u1 - u0) / runLength), Math.max(.2, (v1 - v0) / boardPeriod));
        tex.offset.set(u0 / runLength, v0 / boardPeriod);
      } else {
        tex.repeat.set(Math.max(.2, (u1 - u0) / boardPeriod), Math.max(.2, (v1 - v0) / runLength));
        tex.offset.set(u0 / boardPeriod, v0 / runLength);
      }
      var registry=W.ThermoAspenTimberMaterialsV1,app=registry&&registry.getAppearance(registry.resolveSpecies(state.sceneData,'wall'),'wall');
      var mat = trackMaterial(new T.MeshStandardMaterial({ map: tex,color:app?app.tint:0xffffff,roughness:app?app.roughness:.88, metalness: 0, side: T.DoubleSide }));
      wallPlane(group, wall, u1 - u0, v1 - v0, mat, um, vm, dims);
    }
    ops.forEach(function (o) { addOpening(group, o, dims); });
  }

  function addRoomShell(dims) {
    var wall = state.sceneData && state.sceneData.cladding && state.sceneData.cladding.wall || {};
    var direction = String(wall.direction || 'vertical').toLowerCase().indexOf('horiz') === 0 ? 'horizontal' : 'vertical';
    var coverage = mm(wall.coverageMm || 110);
    woodCanvasRole='wall';var base = woodCanvas(direction);
    WALLS.forEach(function (w) { addWall(w, dims, base, direction, coverage); });

    var floorMat = trackMaterial(new T.MeshStandardMaterial({ color: 0x4b4a47, roughness: .96, metalness: 0, side: T.DoubleSide }));
    floorMesh = new T.Mesh(trackGeometry(new T.PlaneGeometry(dims.L, dims.W)), floorMat);
    floorMesh.rotation.x = -Math.PI / 2; floorMesh.position.set(dims.L / 2, .003, dims.W / 2); floorMesh.receiveShadow = true; roomGroup.add(floorMesh);

    var ceilingDirection = state.sceneData && state.sceneData.cladding && state.sceneData.cladding.ceiling && state.sceneData.cladding.ceiling.direction || 'perpendicular';
    woodCanvasRole='ceiling';var ceilingTex = woodCanvas(String(ceilingDirection).toLowerCase().indexOf('parallel') >= 0 ? 'horizontal' : 'vertical');
    var ceilingSettings=state.sceneData&&state.sceneData.cladding&&state.sceneData.cladding.ceiling||{};
    var ceilingPeriod=Math.max(.05,mm(ceilingSettings.coverageMm||110))*8;
    if(W.ThermoAspenTimberMaterialsV1){
      if(String(ceilingDirection).toLowerCase().indexOf('parallel')>=0)ceilingTex.repeat.set(Math.max(1,dims.L/1.4),Math.max(.2,dims.W/ceilingPeriod));
      else ceilingTex.repeat.set(Math.max(.2,dims.L/ceilingPeriod),Math.max(1,dims.W/1.4));
    }else ceilingTex.repeat.set(Math.max(1, dims.L / 1.5), Math.max(1, dims.W / 1.5));
    var registry=W.ThermoAspenTimberMaterialsV1,ceilingAppearance=registry&&registry.getAppearance(registry.resolveSpecies(state.sceneData,'ceiling'),'ceiling');
    var ceilingMat = trackMaterial(new T.MeshStandardMaterial({ map: ceilingTex,color:ceilingAppearance?ceilingAppearance.tint:0xffffff,roughness:ceilingAppearance?ceilingAppearance.roughness:.9,metalness:0,side:T.DoubleSide }));
    ceilingMesh = new T.Mesh(trackGeometry(new T.PlaneGeometry(dims.L, dims.W)), ceilingMat);
    ceilingMesh.rotation.x = Math.PI / 2; ceilingMesh.position.set(dims.L / 2, dims.H - .008, dims.W / 2); ceilingMesh.receiveShadow = true; roomGroup.add(ceilingMesh);
  }

  function addBenches() {
    benchGroup = new T.Group(); benchGroup.name = 'benches'; roomGroup.add(benchGroup);
    var g = state.sceneData && state.sceneData.benchGeometry;
    if (!g || !Array.isArray(g.members)) return;
    var mats = Object.create(null);
    function benchMat(member) {
      var type=String(member.assemblyType||'member'),size=member.world&&member.world.sizeMm||{},vertical=member.orientation==='vertical',grain=vertical||Number(size.z)>Number(size.x)?'vertical':'horizontal';
      var seedId=String(member.id||type),hash=2166136261;for(var h=0;h<seedId.length;h++){hash^=seedId.charCodeAt(h);hash=Math.imul(hash,16777619);}
      var seed=200+((hash>>>0)%997),key=type+'|'+grain+'|'+((hash>>>0)%7);
      if (mats[key]) return mats[key];
      var color = 0xe1c19e, rough = .84;
      if (type.indexOf('backrest') >= 0) color = 0xd4ac85;
      if (type.indexOf('fascia') >= 0) color = 0xcda57e;
      if (type.indexOf('skirt-backing') >= 0) { color = 0x211a16;rough=1; }
      var registry=W.ThermoAspenTimberMaterialsV1;
      if(registry&&type.indexOf('skirt-backing')<0){
        var species=registry.resolveSpecies(state.sceneData,'bench'),app=registry.getAppearance(species,'bench');
        var tx=trackTexture(registry.createMemberTexture(T,renderer,{role:'bench',speciesId:species,seed:seed,direction:grain}));
        var baselineRough=type==='surface'?.84:type==='fascia'||type==='high-backrest'?.87:.86;
        var materialRough=species==='thermoaspen'?Math.max(.76,Math.min(.90,baselineRough-.035)):app.roughness;
        mats[key]=trackMaterial(new T.MeshStandardMaterial({map:tx,color:app.tint,roughness:materialRough,metalness:0}));
      }else mats[key] = trackMaterial(new T.MeshStandardMaterial({ color: color, roughness: rough, metalness: 0 }));
      return mats[key];
    }
    g.members.forEach(function (m) {
      if (!m || m.render === false || !m.world || !m.world.centerMm || !m.world.sizeMm) return;
      var c = m.world.centerMm, s = m.world.sizeMm, sx = mm(s.x), sy = mm(s.y), sz = mm(s.z);
      if (!(sx > 0 && sy > 0 && sz > 0)) return;
      var mesh = new T.Mesh(trackGeometry(new T.BoxGeometry(sx, sy, sz)), benchMat(m));
      mesh.position.set(mm(c.x), mm(c.y), mm(c.z)); mesh.castShadow = true; mesh.receiveShadow = true;
      mesh.userData.thermoAspenBenchMemberId = m.id || ''; mesh.userData.thermoAspenAssemblyType = m.assemblyType || '';
      mesh.userData.editorBenchBounds={center:{x:mm(c.x),y:mm(c.y),z:mm(c.z)},size:{x:sx,y:sy,z:sz}};
      benchGroup.add(mesh);
    });
  }

  function addStove(dims) {
    stoveGroup = new T.Group(); stoveGroup.name = 'stove'; roomGroup.add(stoveGroup);
    var s = state.sceneData && state.sceneData.layout && state.sceneData.layout.stove;
    if (!s || WALLS.indexOf(s.wall) < 0) return;
    var ph = window.ThermoAspenStovePlaceholderV1; if (!ph) throw new Error('ThermoAspenStovePlaceholderV1 is missing: load thermoaspen-stove-placeholder-v1.js first');
    var sw = mm(s.widthMm || ph.widthMm), sd = mm(s.depthMm || ph.depthMm), sh = mm(s.heightMm || ph.heightMm), gap = mm(s.gapMm || ph.rearGapMm), o = mm(s.offsetMm);
    var x = dims.L / 2, z = dims.W / 2;
    if (s.wall === 'left') { x = gap + sw / 2; z = o + sd / 2; }
    else if (s.wall === 'right') { x = dims.L - gap - sw / 2; z = o + sd / 2; }
    else if (s.wall === 'back') { x = o + sw / 2; z = gap + sd / 2; }
    else { x = o + sw / 2; z = dims.W - gap - sd / 2; }

    // Match the established Interior Viewer stove: steel body, recessed top
    // stone tray, irregular stones that sit into the tray, and a room-facing
    // dark glass/firebox panel. The previous workspace body stopped 100 mm
    // below the stones, which made the stones appear to float.
    var steel = trackMaterial(new T.MeshStandardMaterial({ color: 0x454b4e, roughness: .40, metalness: .76 }));
    var black = trackMaterial(new T.MeshStandardMaterial({ color: 0x1d2021, roughness: .62, metalness: .48 }));
    var stone = trackMaterial(new T.MeshStandardMaterial({ color: 0x5c5a57, roughness: .98, metalness: 0 }));
    var glass = trackMaterial(new T.MeshPhysicalMaterial({ color: 0x2a211d, roughness: .16, metalness: .12, transparent: true, opacity: .82, transmission: .03, thickness: .01, side: T.DoubleSide }));

    var bodyH = Math.max(.20, sh - .12);
    var body = new T.Mesh(trackGeometry(new T.BoxGeometry(sw, bodyH, sd)), steel);
    body.position.set(x, bodyH / 2, z); body.castShadow = true; body.receiveShadow = true; stoveGroup.add(body);

    var tray = new T.Mesh(trackGeometry(new T.BoxGeometry(sw * .90, .11, sd * .90)), black);
    tray.position.set(x, sh - .065, z); tray.castShadow = true; tray.receiveShadow = true; stoveGroup.add(tray);

    function pr(k) { var q = Math.sin(k * 12.9898 + 78.233) * 43758.5453; return q - Math.floor(q); }
    var sg = trackGeometry(new T.DodecahedronGeometry(.055, 0));
    for (var i = -2; i <= 2; i++) for (var j = -2; j <= 2; j++) {
      if (Math.abs(i) === 2 && Math.abs(j) === 2 && ((i + j) & 1)) continue;
      var m = new T.Mesh(sg, stone);
      m.position.set(x + i * sw * .15, sh + .015 + pr(i * 11 + j * 7) * .035, z + j * sd * .15);
      m.scale.set(1 + .20 * pr(i + 3), .75 + .22 * pr(j + 8), 1 + .18 * pr(i * j + 4));
      m.rotation.set(pr(i) * 2, pr(j) * 2, pr(i + j) * 2); m.castShadow = true; stoveGroup.add(m);
    }

    // Room-facing firebox panel. Keep it restrained in the wall editor while
    // giving the stove a readable front rather than a featureless black box.
    var fw = Math.min(.29, (s.wall === 'left' || s.wall === 'right' ? sd : sw) * .64);
    var fh = Math.min(.25, sh * .34), cy = sh * .38, frameT = .022, faceT = .025;
    function addFaceBox(w,h,d,mat,px,py,pz){var q=new T.Mesh(trackGeometry(new T.BoxGeometry(w,h,d)),mat);q.position.set(px,py,pz);q.castShadow=true;stoveGroup.add(q);return q;}
    if (s.wall === 'left' || s.wall === 'right') {
      var fx = x + (s.wall === 'left' ? sw/2 + .014 : -sw/2 - .014);
      addFaceBox(faceT, fh, fw, black, fx, cy, z);
      addFaceBox(faceT + .004, fh * .82, fw * .82, glass, fx + (s.wall === 'left' ? .003 : -.003), cy, z);
      addFaceBox(faceT + .006, frameT, fw + frameT*2, black, fx, cy+fh/2+frameT/2, z);
      addFaceBox(faceT + .006, frameT, fw + frameT*2, black, fx, cy-fh/2-frameT/2, z);
    } else {
      var fz = z + (s.wall === 'back' ? sd/2 + .014 : -sd/2 - .014);
      addFaceBox(fw, fh, faceT, black, x, cy, fz);
      addFaceBox(fw * .82, fh * .82, faceT + .004, glass, x, cy, fz + (s.wall === 'back' ? .003 : -.003));
      addFaceBox(fw + frameT*2, frameT, faceT + .006, black, x, cy+fh/2+frameT/2, fz);
      addFaceBox(fw + frameT*2, frameT, faceT + .006, black, x, cy-fh/2-frameT/2, fz);
    }
  }

  function rebuildScene(reason) {
    if (!renderer || !scene || !validScene(state.sceneData)) return;
    var wasGuidedSwitch = wallSwitchPending;
    cancelFocusedWallSwitch();
    if (wasGuidedSwitch) updateChrome();
    fadedBenchMembers = [];
    disposeSceneContent();
    readOpenings();
    var dims = roomM();
    roomGroup = new T.Group(); roomGroup.name = 'thermoaspen-interior-workspace-room'; scene.add(roomGroup);
    addRoomShell(dims);
    addBenches();
    addStove(dims);
    updateVisibility();
    setCameraForCurrent(false);
    state.revision++;
    state.lastAction = reason || 'rebuild';
    // setCameraForCurrent(false) already rendered the refreshed scene if visible.
    emitUpdated();
  }

  function scheduleRebuild(reason) {
    if (!state.ready) return;
    clearTimeout(rebuildTimer);
    rebuildTimer = W.setTimeout(function () { rebuildScene(reason || 'update'); }, 70);
  }

  function oppositeWall(wall) { return wallAt(wallIndex(wall) + 2); }
  function isAdjacent(wall, active) {
    var d = Math.abs(wallIndex(wall) - wallIndex(active));
    return d === 1 || d === 3;
  }
  // The focused elevation has a foreground camera cut. A very long bench
  // plank can straddle that near plane and appear as a one-pixel streak across
  // the wall. Crop only the DISPLAY mesh to just behind the near-plane cut.
  // The actual canonical bench geometry and manufacturing records are intact.
  function updateFocusBenchCrop(){
    if(!benchGroup)return;
    var focus=state.mode==='wall',d=roomM(),wall=state.activeWall;
    var along=(wall==='left'||wall==='right')?'x':'z';
    var opposite=(wall==='front'||wall==='right');
    var depth=along==='x'?d.L:d.W;
    var maxDepth=depth*.52; // Inside the camera's .56-depth visible envelope.
    benchGroup.children.forEach(function(m){
      var base=m&&m.userData&&m.userData.editorBenchBounds;
      if(!base)return;
      m.position.set(base.center.x,base.center.y,base.center.z);
      m.scale.set(1,1,1);m.visible=true;
      if(!focus)return;
      var size=base.size[along],coord=base.center[along];
      var centreDepth=opposite?depth-coord:coord;
      var nearEnd=centreDepth-size/2,farEnd=centreDepth+size/2;
      if(nearEnd>=maxDepth){m.visible=false;return;}
      if(farEnd>maxDepth){
        var visibleSize=maxDepth-nearEnd;
        if(visibleSize<.015){m.visible=false;return;}
        var visibleCentreDepth=nearEnd+visibleSize/2;
        m.scale[along]=Math.min(1,visibleSize/size);
        m.position[along]=opposite?depth-visibleCentreDepth:visibleCentreDepth;
      }
    });
  }

  function updateVisibility() {
    if (!roomGroup) return;
    WALLS.forEach(function (w) {
      if (!wallGroups[w]) return;
      wallGroups[w].visible = state.mode === 'wall' ? w === state.activeWall : w !== oppositeWall(state.activeWall);
    });
    if (floorMesh) floorMesh.visible = true;
    if (ceilingMesh) ceilingMesh.visible = state.mode !== 'wall';
    windowLabelSprites.forEach(function(label){if(label)label.visible=state.mode!=='wall';});
    updateFocusBenchCrop();
  }

  function resize(preservePose, deferRender) {
    if (!renderer || !camera || !ui.viewport) return;
    var w = Math.max(1, ui.viewport.clientWidth), h = Math.max(1, ui.viewport.clientHeight);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    // Changing room/wall mode changes the reserved controls margin. Do not
    // cancel the camera entry/exit animation merely because that resizes it.
    if (preservePose !== true && !state.transitioning) setCameraForCurrent(false);
    else if (deferRender !== true) render();
  }

  function fitDistance(span, height, fovDeg, aspect, pad) {
    var vf = fovDeg * Math.PI / 180, hf = 2 * Math.atan(Math.tan(vf / 2) * Math.max(.25, aspect));
    var dv = (height / 2) / Math.tan(vf / 2), dh = (span / 2) / Math.tan(hf / 2);
    return Math.max(dv, dh) * (pad || 1.08);
  }

  function isCompactWallEdit(mode) {
    if (mode !== 'wall') return false;
    try { if (W.matchMedia) return W.matchMedia('(max-width: 680px)').matches; } catch (e) {}
    return !!(ui.viewport && ui.viewport.clientWidth <= 680);
  }

  // The carousel retains its existing camera composition. On a compact/mobile
  // focused-wall edit, keep the SAME lens and viewing direction but move along
  // the wall normal just enough for the selected wall to slightly overfill the
  // viewport width. This removes the empty side gutters without a zoom animation
  // or an elevation-style camera jump. The edit view is therefore a tight crop
  // of the same wall context, while Done returns to the normal carousel framing.
  // All values are presentation-only: no room/bench/project geometry is changed.
  function cameraPose(wall, mode) {
    var dims = roomM(), span = wallSpanM(wall, dims), aspect = Math.max(.25, camera && camera.aspect || 1.5);
    var depth = wall === 'front' || wall === 'back' ? dims.W : dims.L;
    var pad = 1.12;
    var carouselDistance = Math.max(.25, depth * .88);
    var needed = 2 * Math.atan(Math.max(dims.H / 2, span / (2 * aspect)) * pad / carouselDistance) * 180 / Math.PI;
    var fov = clamp(needed, 43, 80);
    carouselDistance = Math.max(carouselDistance, fitDistance(span, dims.H, fov, aspect, pad));

    var compactEdit = isCompactWallEdit(mode);
    var distance = carouselDistance;
    if (compactEdit) {
      // Preserve the carousel optical lens. Only crop by moving on the wall normal.
      // 1.018 means the physical wall projects about 1.8% wider than the viewport,
      // preventing pale/empty strips at either side while leaving the wall edges
      // visually defined by the inset overlay border.
      var wallFill = 1.018;
      var vf = fov * Math.PI / 180;
      var hf = 2 * Math.atan(Math.tan(vf / 2) * aspect);
      var tanHalfH = Math.max(.0001, Math.tan(hf / 2));
      distance = Math.max(.30, (span / 2) / (tanHalfH * wallFill));
    }

    // Cut away foreground room geometry while retaining the selected wall.
    var near = Math.max(.02, distance - depth * .56);
    var y = dims.H * .51 + .20, targetY = dims.H * .49, pos, target;
    if (wall === 'back') { pos = [dims.L / 2, y, distance]; target = [dims.L / 2, targetY, 0]; }
    else if (wall === 'front') { pos = [dims.L / 2, y, dims.W - distance]; target = [dims.L / 2, targetY, dims.W]; }
    else if (wall === 'left') { pos = [distance, y, dims.W / 2]; target = [0, targetY, dims.W / 2]; }
    else { pos = [dims.L - distance, y, dims.W / 2]; target = [dims.L, targetY, dims.W / 2]; }

    // Carousel keeps the familiar slight off-centre composition. Compact wall
    // editing is centred so both wall edges meet/overlap the viewport edges.
    if (!compactEdit) {
      var lateral = Math.min(span * .025, .08);
      if (wall === 'back' || wall === 'front') pos[0] += lateral;
      else pos[2] -= lateral;
    }
    return { position: pos, target: target, fov: fov, near: near };
  }

  function quaternionFor(pose) {
    var temp = new T.PerspectiveCamera();
    temp.position.set(pose.position[0], pose.position[1], pose.position[2]);
    temp.lookAt(pose.target[0], pose.target[1], pose.target[2]);
    return temp.quaternion.clone();
  }

  function cancelCameraTransition() {
    cancelAnimationFrame(cameraFrame);
    cameraFrame = 0;
    state.transitioning = false;
  }

  function animateCamera(pose, animate, done, durationMs) {
    if (!camera) return;
    cancelCameraTransition();
    var endPos = new T.Vector3(pose.position[0], pose.position[1], pose.position[2]);
    var endQuat = quaternionFor(pose), endFov = pose.fov, endNear = pose.near;
    if (!animate || REDUCED_MOTION) {
      state.transitioning = false;
      camera.position.copy(endPos); camera.quaternion.copy(endQuat); camera.fov = endFov;
      camera.near = endNear; camera.updateProjectionMatrix(); render();
      if (done) done(); return;
    }
    var startTime = performance.now(), duration = durationMs || (state.mode === 'wall' ? 430 : 360);
    var startPos = camera.position.clone(), startQuat = camera.quaternion.clone(), startFov = camera.fov, startNear = camera.near;
    state.transitioning = true;
    function frame(now) {
      var t = clamp((now - startTime) / duration, 0, 1), e = t * t * t * (10 + t * (-15 + 6 * t));
      camera.position.lerpVectors(startPos, endPos, e); camera.quaternion.slerpQuaternions(startQuat, endQuat, e);
      camera.fov = startFov + (endFov - startFov) * e;
      camera.near = startNear + (endNear - startNear) * e;
      camera.updateProjectionMatrix(); render();
      if (t < 1) cameraFrame = requestAnimationFrame(frame);
      else { cameraFrame = 0; state.transitioning = false; if (done) done(); }
    }
    cameraFrame = requestAnimationFrame(frame);
  }

  function setCameraForCurrent(animate, done, durationMs) {
    if (!camera || !validScene(state.sceneData)) return;
    animateCamera(cameraPose(state.activeWall, state.mode), animate, done, durationMs);
  }

  function render() {
    // This canvas is fully covered by the normal 3D preview while closed.
    // Do not submit hidden WebGL frames; the next open always refreshes the pose.
    if (state.editorVisible && renderer && scene && camera) renderer.render(scene, camera);
  }

  function setActiveWall(wall, opts) {
    opts = opts || {};
    wall = String(wall || '').toLowerCase();
    if (WALLS.indexOf(wall) < 0 || state.mode !== 'room' || wallSwitchPending || state.transitioning) return false;
    if (state.windowDraftActive) return blockDraftNavigation(opts.reason || 'wall-select');
    if (wall === state.activeWall && !opts.force) { updateChrome(); return true; }
    state.activeWall = wall; state.revision++; state.lastAction = opts.reason || 'wall-select';
    updateVisibility(); updateChrome();
    setCameraForCurrent(opts.animate !== false, function () { announce('Facing ' + wallLabel(wall) + (state.editorVisible ? '. Add or select a window on this wall.' : '.')); });
    emitUpdated();
    return true;
  }

  // Adjacent wall is relative to the viewer, NOT the global room coordinate system.
  function adjacentWall(wall, delta) {
    var rightOf = { back: 'right', right: 'front', front: 'left', left: 'back' };
    var leftOf = { back: 'left', left: 'front', front: 'right', right: 'back' };
    return delta < 0 ? leftOf[wall] : rightOf[wall];
  }
  function turn(delta, reason) {
    if (state.mode !== 'room' || state.transitioning) return false;
    return setActiveWall(adjacentWall(state.activeWall, delta), { animate: true, reason: reason || 'turn' });
  }

  // Guided wall switching uses ONE camera timeline, not three separately eased
  // animations. Four key poses describe the same visible route:
  // focused wall -> its room view -> adjacent room view -> adjacent focused wall.
  // Continuous cubic-Hermite POSITION/FOV motion maintains velocity through the
  // intermediate poses. Only heading eases within the actual 90-degree turn.
  // All changes below are presentation state; project/opening data is untouched.
  function cancelFocusedWallSwitch() {
    if (!wallSwitchPending) return;
    wallSwitchEpoch++; // invalidate even a previously queued animation frame
    cancelCameraTransition();
    wallSwitchPending = false;
    wallSwitchDestination = null;
    wallSwitchPhase = null;
    if (ui.canvas) ui.canvas.classList.remove('is-switching-wall');
    if (ui.stage) ui.stage.setAttribute('aria-busy', 'false');
  }

  function switchFocusedWall(delta, reason) {
    if (state.mode !== 'wall' || wallSwitchPending || state.transitioning || !state.ready || !camera) return false;
    var from = state.activeWall, destination = adjacentWall(from, delta);
    if (!destination) return false;
    var epoch = ++wallSwitchEpoch;
    wallSwitchPending = true;
    wallSwitchDestination = destination;
    wallSwitchPhase = 'pullback';

    // Remember the true starting camera so a recently finished action or a
    // different viewport aspect cannot cause a first-frame camera jump.
    var source = {
      position: [camera.position.x, camera.position.y, camera.position.z],
      quaternion: camera.quaternion.clone(), fov: camera.fov, near: camera.near
    };
    state.mode = 'room';
    updateVisibility(); updateChrome(); resize(true);
    announce('Turning from ' + wallLabel(from) + ' to ' + wallLabel(destination) + '.');

    var roomFrom = cameraPose(from, 'room');
    var roomTo = cameraPose(destination, 'room');
    var focusTo = cameraPose(destination, 'wall');
    var keyframes = [source, roomFrom, roomTo, focusTo];
    var q = [source.quaternion, quaternionFor(roomFrom), quaternionFor(roomTo), quaternionFor(focusTo)];
    var values = keyframes.map(function (p) {
      return p.position.concat([p.fov, p.near]);
    });
    // Focused and carousel framing now match, so wall switching is primarily
    // one continuous 90-degree turn with short state-change bookends.
    var boundaries = [0, .08, .92, 1], duration = 650;
    var visibilitySwitched = false;

    function finish() {
      if (epoch !== wallSwitchEpoch || !wallSwitchPending) return;
      cameraFrame = 0;
      state.transitioning = false;
      state.mode = 'wall'; state.activeWall = destination;
      wallSwitchPending = false;
      wallSwitchDestination = null;
      wallSwitchPhase = null;
      state.revision++;
      state.lastAction = reason || 'guided-focused-wall-switch';
      updateVisibility(); updateChrome(); resize(true); render();
      announce(wallLabel(destination) + '. Wall view.');
      emitUpdated();
    }

    if (REDUCED_MOTION) {
      camera.position.set(focusTo.position[0], focusTo.position[1], focusTo.position[2]);
      camera.quaternion.copy(q[3]);
      camera.fov = focusTo.fov; camera.near = focusTo.near;
      camera.updateProjectionMatrix();
      finish();
      return true;
    }

    // Times are expressed as fractions of the single uninterrupted trip.
    // A Catmull-style central derivative, scaled to 82%, gently bends the
    // camera trajectory without the sharp velocity changes of straight lerps.
    function valueAt(channel, section, u) {
      var i = section, h = boundaries[i + 1] - boundaries[i], u2 = u * u, u3 = u2 * u;
      function velocity(k) {
        if (k === 0 || k === 3) return 0; // settle at start and finish only
        return .82 * (values[k + 1][channel] - values[k - 1][channel]) /
          (boundaries[k + 1] - boundaries[k - 1]);
      }
      return (2 * u3 - 3 * u2 + 1) * values[i][channel] +
        (u3 - 2 * u2 + u) * h * velocity(i) +
        (-2 * u3 + 3 * u2) * values[i + 1][channel] +
        (u3 - u2) * h * velocity(i + 1);
    }
    function smootherstep(t) { return t * t * t * (10 + t * (-15 + 6 * t)); }
    var startTime = performance.now();
    state.transitioning = true;
    function frame(now) {
      if (!wallSwitchPending || epoch !== wallSwitchEpoch || !camera) return;
      var t = clamp((now - startTime) / duration, 0, 1);
      // Discrete scene/chrome changes happen at the already established
      // camera waypoints. They do not interrupt the continuous camera path.
      if (t >= boundaries[1] && wallSwitchPhase === 'pullback') {
        wallSwitchPhase = 'turn';
        state.activeWall = destination;
        updateChrome();
      }
      // Exchange the hidden walls halfway through the actual turn, when
      // they are leaving/entering the field of view, not at turn start.
      if (!visibilitySwitched && t >= (boundaries[1] + boundaries[2]) / 2) {
        visibilitySwitched = true;
        updateVisibility();
      }
      if (t >= boundaries[2] && wallSwitchPhase === 'turn') {
        wallSwitchPhase = 'enter';
        state.mode = 'wall';
        updateChrome(); resize(true);
      }
      var i = t < boundaries[1] ? 0 : (t < boundaries[2] ? 1 : 2);
      var u = clamp((t - boundaries[i]) / (boundaries[i + 1] - boundaries[i]), 0, 1);
      camera.position.set(valueAt(0, i, u), valueAt(1, i, u), valueAt(2, i, u));
      // Orientation is stable during pullback/entry and rotates smoothly in
      // the middle. Positional motion still flows through both waypoints.
      camera.quaternion.slerpQuaternions(q[i], q[i + 1], smootherstep(u));
      camera.fov = clamp(valueAt(3, i, u), 22, 84);
      camera.near = Math.max(.02, valueAt(4, i, u));
      camera.updateProjectionMatrix(); render();
      if (t < 1) cameraFrame = requestAnimationFrame(frame);
      else finish();
    }
    cameraFrame = requestAnimationFrame(frame);
    return true;
  }

  function navigateAdjacent(delta, reason) {
    return state.mode === 'wall' ? switchFocusedWall(delta, reason) : turn(delta, reason);
  }

  function enterWall(wall, openingId, opts) {
    opts = opts || {};
    cancelFocusedWallSwitch();
    wall = String(wall || state.activeWall).toLowerCase();
    if (WALLS.indexOf(wall) < 0) return false;
    if (state.mode === 'room' && wall !== state.activeWall) {
      if (openingId) selectOpening(openingId, { reason: 'side-opening-select' });
      return setActiveWall(wall, { animate: true, reason: 'side-wall-click' });
    }
    if (openingId) selectOpening(openingId, { reason: opts.reason || 'wall-enter-opening' });
    state.activeWall = wall; state.mode = 'wall'; state.revision++; state.lastAction = opts.reason || 'wall-enter';
    updateChrome();
    updateVisibility();
    resize(true);
    // Focused editing changes presentation immediately: desktop retains the
    // carousel pose; compact/mobile uses the width-filling crop above. There is
    // deliberately no camera animation on entry.
    setCameraForCurrent(false, function () {
      announce(wallLabel(wall) + ' opened. Press Escape or close to return to the room.');
    });
    emitUpdated();
    return true;
  }

  function closeWall(opts) {
    opts = opts || {};
    if (state.mode !== 'wall' && !wallSwitchPending) return false;
    cancelFocusedWallSwitch();
    state.mode = 'room'; state.revision++; state.lastAction = opts.reason || 'wall-close';
    updateVisibility(); updateChrome();
    resize(true);
    // Restore the normal carousel framing immediately on completion/close.
    setCameraForCurrent(false, function () { announce('Returned to room view facing ' + wallLabel(state.activeWall) + '.'); });
    emitUpdated();
    return true;
  }

  function pick(clientX, clientY) {
    if (!raycaster || !camera || !ui.canvas || !roomGroup) return null;
    var rect = ui.canvas.getBoundingClientRect();
    mouse.x = ((clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1;
    mouse.y = -((clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
    // A cut-away foreground must not remain clickable after being clipped.
    raycaster.near = camera.near;
    raycaster.far = camera.far;

    // The viewing model hides the wall opposite the active wall. Three.js
    // raycasting can still hit an invisible group, and that opposite wall is
    // actually CLOSEST to the camera (we look into the room through it).
    // Pick strictly from the walls currently allowed to receive input.
    var eligible = state.mode === 'wall'
      ? [state.activeWall]
      : [state.activeWall, wallAt(wallIndex(state.activeWall) - 1), wallAt(wallIndex(state.activeWall) + 1)];
    var targets = eligible.map(function (wall) { return wallGroups[wall]; })
      .filter(function (group) { return !!group && group.visible; });
    if (!targets.length) return null;

    // Intersections are returned nearest first: preserve occlusion rather than
    // letting a window on a more distant wall steal a foreground wall click.
    var hits = raycaster.intersectObjects(targets, true);
    for (var i = 0; i < hits.length; i++) {
      var obj = hits[i].object, ud = obj && obj.userData || {};
      if (!obj || obj.visible === false) continue;
      // Raycaster.near uses radial ray distance, whereas the camera near plane
      // is perpendicular to the viewing direction. Reject clipped hits on
      // off-axis adjacent walls using camera-space depth too.
      var cameraHit = hits[i].point.clone().applyMatrix4(camera.matrixWorldInverse);
      if (cameraHit.z > -camera.near + .001 || cameraHit.z < -camera.far) continue;
      if (ud.thermoAspenOpeningId) return {
        openingId: ud.thermoAspenOpeningId,
        type: ud.thermoAspenOpeningType || null,
        wall: ud.thermoAspenWall || null,
        object: obj
      };
      if (ud.thermoAspenWall) return { wall: ud.thermoAspenWall, object: obj };
    }
    return null;
  }

  function onPointerDown(e) {
    if (!state.ready || state.transitioning || (e.pointerType === 'mouse' && e.button !== 0)) return;
    pointer.down = true; pointer.id = e.pointerId; pointer.x = pointer.startX = e.clientX; pointer.y = pointer.startY = e.clientY; pointer.moved = false;
    try { ui.canvas.setPointerCapture(e.pointerId); } catch (x) {}
    ui.canvas.classList.add('is-pressing');
  }
  function onPointerMove(e) {
    if (pointer.down && pointer.id === e.pointerId) {
      pointer.x = e.clientX; pointer.y = e.clientY;
      if (!pointer.moved && Math.hypot(pointer.x - pointer.startX, pointer.y - pointer.startY) > 7) pointer.moved = true;
      if (pointer.moved) ui.canvas.classList.add('is-dragging');
      return;
    }
    var hit = pick(e.clientX, e.clientY);
    if (!ui.canvas) return;
    ui.canvas.classList.toggle('is-clickable', !!hit);
    if (hit && state.mode === 'room') ui.canvas.title = hit.wall && hit.wall !== state.activeWall ? 'Turn to ' + wallLabel(hit.wall) : (state.editorVisible ? 'Edit windows on ' + wallLabel(state.activeWall) : 'Open ' + wallLabel(state.activeWall));
    else if (hit && hit.openingId) ui.canvas.title = 'Select ' + openingLabel(openingById(hit.openingId));
    else ui.canvas.removeAttribute('title');
  }
  function onPointerUp(e) {
    if (!pointer.down || pointer.id !== e.pointerId) return;
    if (wallSwitchPending || state.transitioning) { onPointerCancel(e); return; }
    var dx = e.clientX - pointer.startX, dy = e.clientY - pointer.startY, moved = pointer.moved;
    pointer.down = false; pointer.id = null;
    try { ui.canvas.releasePointerCapture(e.pointerId); } catch (x) {}
    ui.canvas.classList.remove('is-pressing', 'is-dragging');
    if (state.mode === 'room' && moved && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.15) {
      turn(dx < 0 ? 1 : -1, 'swipe');
      e.preventDefault(); return;
    }
    if (moved) return;
    var hit = pick(e.clientX, e.clientY);
    if (!hit) return;
    if (state.mode === 'room') {
      var hitWall = hit.wall || state.activeWall;
      if (hitWall !== state.activeWall) {
        // Side walls are navigation context only. Turn them to the centre before
        // any window can be selected or edited.
        setActiveWall(hitWall, { animate: true, reason: 'side-wall-click' });
      } else if (hit.openingId) {
        // Carousel editing stays in room mode. Selecting a centred-wall opening
        // starts the overlay draft editor; clicking bare wall does not enter a
        // second focused-wall navigation mode.
        selectOpening(hit.openingId, { reason: 'carousel-opening-click' });
      }
    } else if (hit.openingId) {
      selectOpening(hit.openingId, { reason: 'focused-opening-click' });
    }
  }
  function onPointerCancel(e) {
    if (!pointer.down || pointer.id !== e.pointerId) return;
    pointer.down = false; pointer.id = null;
    ui.canvas.classList.remove('is-pressing', 'is-dragging');
  }
  function onKeyDown(e) {
    if (e.key === 'Escape' && state.editorVisible) { e.preventDefault(); setEditorVisible(false, { reason: 'escape' }); return; }
    if (wallSwitchPending || state.transitioning) return;
    if (e.key === 'ArrowLeft') { e.preventDefault(); navigateAdjacent(-1, 'keyboard'); return; }
    if (e.key === 'ArrowRight') { e.preventDefault(); navigateAdjacent(1, 'keyboard'); return; }
    if (e.target === ui.canvas && state.mode === 'room' && (e.key === 'Enter' || e.key === ' ')) {
      if (state.editorVisible) { e.preventDefault(); announce('Use the add marker or select a window on ' + wallLabel(state.activeWall) + '.'); }
      else { e.preventDefault(); enterWall(state.activeWall, null, { animate: true, reason: 'keyboard-enter' }); }
    }
  }

  function onSceneUpdated(e) {
    var d = e && e.detail;
    if (!validScene(d)) return;
    state.sceneData = clone(d);
    readOpenings();
    updateViewerSwitchButton();
    if (state.ready) { showLoading(false); scheduleRebuild('scene-update'); } else if (state.editorVisible) startRenderer();
  }
  function onDesignerUpdated() {
    readOpenings();
    updateViewerSwitchButton();
    if (state.ready) scheduleRebuild('designer-update');
  }
  function onOpeningsUpdated(e) {
    var detail = e && e.detail || {};
    if (detail.selectedWindowId) state.selectedOpeningId = detail.selectedWindowId;
    readOpenings();
    if (state.ready) scheduleRebuild('openings-update');
    updateChrome();
    updateViewerSwitchButton();
  }
  function onOpeningReady() {
    readOpenings();
    if (!state.selectedOpeningId) {
      var api = openingApi();
      if (api && typeof api.getSelectedWindowId === 'function') state.selectedOpeningId = api.getSelectedWindowId();
    }
    if (state.ready) scheduleRebuild('opening-ready');
    updateViewerSwitchButton();
  }
  function onSectionUnload(e) {
    if (e && e.target && ui.root && e.target.contains(ui.root)) destroy();
  }

  function emitReady() {
    dispatch('ThermoAspenInteriorWorkspaceReady', { version: VERSION, mode: state.mode, activeWall: state.activeWall });
  }
  function emitUpdated() {
    dispatch('ThermoAspenInteriorWorkspaceUpdated', getState());
  }

  function getState() {
    return {
      version: VERSION,
      mode: state.mode,
      activeWall: state.activeWall,
      selectedOpeningId: state.selectedOpeningId,
      focusedOpeningId: openingById(state.selectedOpeningId) && openingById(state.selectedOpeningId).wall === state.activeWall ? state.selectedOpeningId : null,
      revision: state.revision,
      ready: state.ready,
      editorVisible: state.editorVisible,
      windowDraftActive: state.windowDraftActive,
      transitioning: state.transitioning || wallSwitchPending,
      openings: clone(state.openings),
      lastAction: state.lastAction
    };
  }

  // The focused-wall editor is a presentation client of this projection API.
  // It cannot mutate 3D geometry or camera position. The millimetre datum
  // agrees with the existing opening meshes for all four interior walls.
  function wallWorldPoint(wall, uMm, yMm) {
    var d = roomM(), u = mm(uMm), y = mm(yMm);
    if (!T) return null;
    if (wall === 'back') return new T.Vector3(u, y, .016);
    if (wall === 'front') return new T.Vector3(u, y, d.W - .016);
    if (wall === 'left') return new T.Vector3(.016, y, u);
    if (wall === 'right') return new T.Vector3(d.L - .016, y, u);
    return null;
  }
  function projectWallPoint(wall, uMm, yMm) {
    if (!state.ready || !camera || !ui.viewport || !T) return null;
    var v = wallWorldPoint(wall, uMm, yMm), w = ui.viewport.clientWidth, h = ui.viewport.clientHeight;
    if (!v || !w || !h) return null;
    camera.updateMatrixWorld(); v.project(camera);
    return {x: (v.x + 1) * w / 2, y: (1 - v.y) * h / 2, depth: v.z,
      visible: isFinite(v.x) && isFinite(v.y) && v.z >= -1 && v.z <= 1};
  }
  function clientToWallPoint(wall, clientX, clientY) {
    if (!state.ready || !camera || !ui.canvas || !T) return null;
    var r = ui.canvas.getBoundingClientRect(), d = roomM();
    if (!r.width || !r.height) return null;
    var xy = new T.Vector2((clientX-r.left)/r.width*2-1, 1-(clientY-r.top)/r.height*2);
    var ray = new T.Raycaster(); ray.setFromCamera(xy, camera);
    var onX = wall === 'left' || wall === 'right';
    var plane = wall === 'back' ? .016 : wall === 'front' ? d.W-.016 : wall === 'left' ? .016 : d.L-.016;
    var dir = onX ? ray.ray.direction.x : ray.ray.direction.z;
    if (Math.abs(dir) < .000001) return null;
    var origin = onX ? ray.ray.origin.x : ray.ray.origin.z;
    var dist = (plane - origin) / dir;
    if (!isFinite(dist) || dist < 0) return null;
    var v = ray.ray.at(dist, new T.Vector3());
    return {offsetMm: Math.round(1000*(onX?v.z:v.x)), heightMm: Math.round(1000*v.y)};
  }
  var fadedBenchMembers = [];
  function restoreBenchOpacity() {
    fadedBenchMembers.forEach(function (m) { if (m && m.userData && m.userData.originalEditorMaterial) {
      m.material = m.userData.originalEditorMaterial; delete m.userData.originalEditorMaterial;
    }});
    fadedBenchMembers = [];
  }
  function setFocusedBenchFade(id) {
    restoreBenchOpacity();
    if (!id || !state.editorVisible || !benchGroup || !camera || !T) { render(); return; }
    var o = openingById(id);
    if (!o || o.wall !== state.activeWall || o.type !== 'window') { render(); return; }
    var corners = [projectWallPoint(o.wall,o.offsetMm,o.sillHeightMm),
      projectWallPoint(o.wall,o.offsetMm+o.frameWidthMm,o.sillHeightMm+o.frameHeightMm)];
    if (!corners[0] || !corners[1]) return;
    var minx = Math.min(corners[0].x,corners[1].x), maxx = Math.max(corners[0].x,corners[1].x);
    var miny = Math.min(corners[0].y,corners[1].y), maxy = Math.max(corners[0].y,corners[1].y);
    var wallDepth = Math.min(corners[0].depth,corners[1].depth);
    benchGroup.updateMatrixWorld(true); camera.updateMatrixWorld(true);
    benchGroup.children.forEach(function(m){
      if (!m || !m.geometry || !m.material) return;
      var box = new T.Box3().setFromObject(m), a=[], p, xs=[],ys=[], depths=[];
      [box.min.x,box.max.x].forEach(function(x){[box.min.y,box.max.y].forEach(function(y){[box.min.z,box.max.z].forEach(function(z){
        p = new T.Vector3(x,y,z).project(camera);xs.push((p.x+1)*ui.viewport.clientWidth/2);
        ys.push((1-p.y)*ui.viewport.clientHeight/2);depths.push(p.z);
      });});});
      // Camera-depth comparison prevents fading furniture behind the actual wall.
      if (Math.min.apply(null,xs)>maxx || Math.max.apply(null,xs)<minx ||
          Math.min.apply(null,ys)>maxy || Math.max.apply(null,ys)<miny ||
          Math.min.apply(null,depths)>=wallDepth-.001) return;
      m.userData.originalEditorMaterial = m.material;
      var mat = trackMaterial(m.material.clone());mat.transparent=true;mat.opacity=.24;mat.depthWrite=false;
      m.material=mat;fadedBenchMembers.push(m);
    });
    render();
  }

  function destroy() {
    cancelFocusedWallSwitch();
    if(windowViewBaseTexture){try{windowViewBaseTexture.dispose();}catch(e){}windowViewBaseTexture=null;}windowViewLoading=false;
    clearTimeout(ui.mountTimer); clearTimeout(ui.embedTimer); clearTimeout(rebuildTimer); cancelAnimationFrame(cameraFrame);
    if (previewStateObserver) { try { previewStateObserver.disconnect(); } catch (e) {} previewStateObserver = null; }
    if (ui.previewStage) {
      ui.previewStage.classList.remove('ti3d-editor-active'); ui.previewStage.classList.remove('ti3d-wall-edit-active'); ui.previewStage.classList.remove('ti3d-exterior-button-visible');
      var previewCard = ui.previewStage.closest && ui.previewStage.closest('.ti3d-card');
      if (previewCard) previewCard.classList.remove('ti3d-wall-edit-card-active');
    }
    if (ui.switchButton && ui.switchButton.parentNode) ui.switchButton.parentNode.removeChild(ui.switchButton);
    if (ui.floorPlanButton && ui.floorPlanButton.parentNode) ui.floorPlanButton.parentNode.removeChild(ui.floorPlanButton);
    if (ui.exteriorButton && ui.exteriorButton.parentNode) ui.exteriorButton.parentNode.removeChild(ui.exteriorButton);
    ui.switchButton = null; ui.floorPlanButton = null; ui.exteriorButton = null; ui.previewCanvas = null; ui.previewPoster = null; ui.previewError = null; ui.previewStage = null;
    W.removeEventListener('ThermoAspenSaunaSceneUpdated', onSceneUpdated);
    W.removeEventListener('ThermoAspenSaunaDesignerUpdated', onDesignerUpdated);
    W.removeEventListener('ThermoAspenOpeningCollectionUpdated', onOpeningsUpdated);
    W.removeEventListener('ThermoAspenOpeningCollectionReady', onOpeningReady);
    D.removeEventListener('shopify:section:unload', onSectionUnload);
    disposeRenderer();
    if (ui.root && ui.root.parentNode) ui.root.parentNode.removeChild(ui.root);
    state.mounted = false;
  }

  var API = {
    version: VERSION,
    getState: getState,
    setActiveWall: function (wall) { return setActiveWall(wall, { animate: true, reason: 'api' }); },
    turnLeft: function () { return navigateAdjacent(-1, 'api'); },
    turnRight: function () { return navigateAdjacent(1, 'api'); },
    enterWall: function (wall, openingId) { return enterWall(wall || state.activeWall, openingId || null, { animate: true, reason: 'api' }); },
    closeWall: function () { return closeWall({ animate: true, reason: 'api' }); },
    showEditor: function () { return setEditorVisible(true, { reason: 'api' }); },
    hideEditor: function () { return setEditorVisible(false, { reason: 'api' }); },
    toggleEditor: function () { return setEditorVisible(!state.editorVisible, { reason: 'api' }); },
    setWindowDraftActive: setWindowDraftActive,
    selectOpening: function (id) { return selectOpening(id, { reason: 'api' }); },
    rebuild: function () { if (state.ready) rebuildScene('api'); },
    projectWallPoint: projectWallPoint,
    clientToWallPoint: clientToWallPoint,
    setFocusedBenchFade: setFocusedBenchFade,
    getRoom: roomMm,
    destroy: destroy
  };
  W.ThermoAspenInteriorWorkspaceV1 = API;

  W.addEventListener('ThermoAspenSaunaSceneUpdated', onSceneUpdated);
  W.addEventListener('ThermoAspenSaunaDesignerUpdated', onDesignerUpdated);
  W.addEventListener('ThermoAspenOpeningCollectionUpdated', onOpeningsUpdated);
  W.addEventListener('ThermoAspenOpeningCollectionReady', onOpeningReady);
  D.addEventListener('shopify:section:unload', onSectionUnload);

  if (validScene(W.ThermoAspenSaunaSceneData)) state.sceneData = clone(W.ThermoAspenSaunaSceneData);
  readOpenings();
  state.activeWall = chooseInitialWall();
  mountWhenReady();
})(window, document);
